/**
 * chartTokens — design-token bridge for Recharts chart widgets.
 *
 * All colours in this file are CSS custom-property references (var(--…)).
 * No hex values appear here; every token resolves at runtime from tokens.css,
 * meaning light ↔ dark theme switches are free — zero JS required.
 *
 * The `token()` helper is a LAST RESORT for Recharts elements that refuse a
 * var() string (e.g. certain SVG attribute setters). Prefer passing the
 * `var(--token)` string directly, as HealthSparkline does.
 *
 * sectionCardStyle / insetWellStyle — shared card anatomy for every dashboard
 * widget (DESIGN-SPEC.md §2.11): borderless luminance-stepped surfaces, no
 * card-level box-shadow. Replaces the old bordered/shadowed card idiom
 * (`background: var(--surface); border: 1px solid var(--border); boxShadow:
 * var(--shadow-2)`) that read as "old app, new colors".
 */
import type { CSSProperties } from 'react';

/** Section-card chrome (Donut / ThroughputBar / CrsWithOpenRaidsBar / CapacityPanel / WidgetBoundary). */
export function sectionCardStyle(extra?: CSSProperties): CSSProperties {
  return {
    background: 'var(--surface-1)',
    border: 'none',
    borderRadius: 'var(--radius-panel)',
    boxShadow: 'none',
    padding: '20px',
    boxSizing: 'border-box',
    ...extra,
  };
}

/** Inset well — one luminance step DOWN from its parent card (§2.11), for sub-content wells. */
export function insetWellStyle(extra?: CSSProperties): CSSProperties {
  return {
    background: 'var(--inset)',
    borderRadius: 'var(--radius-inset)',
    padding: '10px 12px',
    boxSizing: 'border-box',
    ...extra,
  };
}

/**
 * Hatched (candy-stripe) meter track background — §1.8/§2.10 signature
 * element. `hue` must be a resolvable color (a var(--…) or hex); the stripe
 * itself is a 45° repeating-linear-gradient at 3–4px period, alternating the
 * fill hue with a ~20%-darker mix of the same hue over a same-hue 30–35%-
 * opacity track (never a flat/gray fill).
 */
export function hatchedFillStyle(hue: string): CSSProperties {
  return {
    backgroundImage: `repeating-linear-gradient(45deg, ${hue} 0px, ${hue} 3px, color-mix(in srgb, ${hue} 78%, black) 3px, color-mix(in srgb, ${hue} 78%, black) 6px)`,
  };
}

export function hatchedTrackStyle(hue: string): CSSProperties {
  return {
    background: `color-mix(in srgb, ${hue} 32%, transparent)`,
  };
}

/**
 * Resolve a CSS custom property to a concrete colour string.
 * Only use this where Recharts cannot accept a var() reference directly.
 * Returns an empty string in SSR / non-browser environments.
 */
export function token(name: string): string {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

// ---------------------------------------------------------------------------
// Work item lifecycle palette
// DELIBERATELY excludes --health-red so the work-item-status donut never
// colour-collides with the health-red "Critical" sector in the RAG donut.
//
// ON_HOLD decision: --health-amber is used here. The work-item-status donut and the
// RAG donut sit side-by-side on the console. Both use amber, but amber appears
// once per donut in a DIFFERENT visual context (paused work vs. at-risk
// project), so the semantic collision is acceptable — amber's shared meaning
// is "caution", which is correct for both. If a future design review finds it
// confusing, swap ON_HOLD to 'var(--seedz-violet)' here.
// ---------------------------------------------------------------------------
export const STATUS_COLORS: Record<string, string> = {
  NOT_PLANNED: 'var(--text-subtle)',
  PLANNED:     'var(--accent)',        // teal/violet — queued / upcoming
  ONGOING:     'var(--brand)',         // emerald/teal — active work in flight
  ON_HOLD:     'var(--health-amber)',  // amber — paused / caution
  DONE:        'var(--health-green)', // green — completed
  CANCELLED:   'var(--health-slate)', // slate — dropped, muted (NOT red)
};

export const RAG_COLORS: Record<string, string> = {
  GREEN: 'var(--health-green)',
  AMBER: 'var(--health-amber)',
  RED:   'var(--health-red)',
};

export const STATUS_LABELS: Record<string, string> = {
  NOT_PLANNED: 'Not planned',
  PLANNED:     'Planned',
  ONGOING:     'Ongoing',
  ON_HOLD:     'On hold',
  DONE:        'Done',
  CANCELLED:   'Cancelled',
};

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}
